import { FunctionComponent, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Drawer, List, Stack, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import LeviantLogo from '@/assets/logos/LeviantLogo';
import useAuth from '@/shared/context/auth-context';
import SideNavigationItem from './SideNavigationItem';
import { SideNavigationProps } from './types';
import UserInfo from './UserInfo';
import './side-navigation.scss';

const SideNavigation: FunctionComponent<SideNavigationProps> = ({
	primary,
	secondary,
}): ReactElement => {
	const { logout, user, isSpoofing, stopSpoofing, isStoppingSpoofing } = useAuth();

	return (
		<Drawer
			variant="permanent"
			anchor="left"
			role="navigation"
			aria-label="Main navigation"
			className="side-navigation"
			sx={{ displayPrint: 'none' }}
		>
			<Stack component="nav" className="side-navigation" direction="column">
				<Box component="header" className="side-navigation__header">
					<Link to="/" aria-label="Homepage">
						<LeviantLogo />
					</Link>
				</Box>
				{isSpoofing && (
					<Box
						sx={{
							backgroundColor: 'warning.main',
							color: 'warning.contrastText',
							p: 1.5,
							mx: 1,
							borderRadius: 1,
							textAlign: 'center',
						}}
					>
						<PersonOffIcon sx={{ fontSize: 20, mb: 0.5 }} />
						<Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
							Spoofing User:
						</Typography>
						<Typography variant="caption" component="div" sx={{ mb: 1 }}>
							{user?.firstName} {user?.lastName}
						</Typography>
						<Button
							variant="contained"
							size="small"
							color="inherit"
							onClick={() => stopSpoofing()}
							disabled={isStoppingSpoofing}
							sx={{
								color: 'warning.main',
								backgroundColor: 'warning.contrastText',
								'&:hover': {
									backgroundColor: 'grey.100',
								},
							}}
						>
							Stop Spoofing
						</Button>
					</Box>
				)}
				<Stack
					className="side-navigation__main-navigation-container"
					direction="column"
					justifyContent="space-between"
				>
					<List>
						{primary.map((primaryNavigationItem, key) => (
							<SideNavigationItem
								{...primaryNavigationItem}
								key={`primary-navigation-item-${key}`}
							/>
						))}
					</List>
					<List>
						{secondary.map((secondaryNavigationItem, key) => (
							<SideNavigationItem
								{...secondaryNavigationItem}
								key={`secondary-navigation-item-${key}`}
							/>
						))}
						<SideNavigationItem itemTitle="Logout" icon={<LogoutIcon />} onClick={logout} />
						{user && <UserInfo user={user} />}
					</List>
				</Stack>
			</Stack>
		</Drawer>
	);
};

export default SideNavigation;
